import { TbMailPlus } from 'react-icons/tb'
import { statusFieldView } from '@/Ship/Components/UI/StatusBadge/statusField'
import { PhoneFieldTemplate } from '@/Ship/Templates/Form/PhoneFieldTemplate'

import { USER_ROLE_OPTIONS } from '../Contracts/UserRole'
import { USER_STATUS_OPTIONS } from '../Contracts/UserStatus'
import { normalizeRolesPayload } from '../Helpers/normalizeRolesPayload'
import { UserEditTemplate } from '../Templates/UserEditTemplate'

import type { IResourceConfig } from '@/Core/Contracts/UI'

export const UserResource: IResourceConfig = {
    title: 'Співробітники',
    fields: {
        id: {
            label: 'ID',
            type: 'number',
            /** У фільтрі: оператор (=, >, ≥, <, ≤) або діапазон від–до */
            isFilterRange: true,
        },
        /** Лише для показу (таблиця, фільтр): бекенд збирає його з Імені, По-батькові та Прізвища */
        fullName: {
            label: 'ПІБ',
            type: 'string',
        },
        lastName: {
            label: 'Прізвище',
            type: 'string',
            isRequired: true,
            inputProps: { maxLength: 100 },
        },
        firstName: {
            label: "Ім'я",
            type: 'string',
            isRequired: true,
            inputProps: { maxLength: 100 },
        },
        middleName: {
            label: 'По-батькові',
            type: 'string',
            inputProps: { maxLength: 100 },
        },
        email: {
            label: 'E-Mail',
            type: 'email',
        },
        phone: {
            label: 'Мобільний телефон',
            type: 'phone',
            isRequired: true,
            /** У перегляді +380 (XX) XXX-XX-XX; data-* — щоб менеджер паролів не вважав поле логіном */
            template: PhoneFieldTemplate,
            inputProps: { 'data-lpignore': 'true', 'data-1p-ignore': 'true', 'data-bwignore': 'true' },
        },
        phones: {
            label: 'Телефони',
            isMultiple: true,
            type: 'phone',
            template: PhoneFieldTemplate,
        },
        plainPassword: {
            label: 'Пароль',
            type: 'password',
            inputProps: {
                autoComplete: 'new-password',
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-bwignore': 'true',
            },
        },
        position: {
            label: 'Посада',
            type: 'select',
            resource: 'Setting.ListPosition',
            inputProps: {
                filter: {
                    isActive: true,
                },
            },
        },
        dateOfBirth: {
            label: 'Дата народження',
            type: 'date',
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: USER_STATUS_OPTIONS,
            /** У картці запису статус малює Ship-бейдж із меню зміни */
            view: statusFieldView,
            /** У фільтрі можна обрати кілька статусів */
            isFilterMultiple: true,
            getChangeUrl: ({ item }) => `/services/users/${item.uuid}/change_status`,
            confirmOnValues: ['deleted'],
            confirmText:
                'Видалений користувач не буде відображатись у реєстрах. Дія видалення користувача фінальна і видалений користувач не може бути відновлений у системі. Ви впевнені, що хочете видалити користувача назавжди?',
            onAfterChange: ({ option, crudContext }: { option: { value: string }; crudContext: any }) => {
                if (option.value === 'deleted') {
                    crudContext?.actions?.reload?.()
                }
            },
            depFn: ({ field, formContext }) => {
                if (formContext.contextKey !== 'filterFormContext') {
                    field.mergeInputProps({
                        disabled: true,
                    })
                }
            },
        },
        roles: {
            label: 'Ролі',
            isRequired: true,
            isMultiple: false,
            type: 'select',
            options: USER_ROLE_OPTIONS,
        },
        filterRoles: {
            label: 'Ролі',
            isMultiple: true,
            /** У фільтрі кілька ролей означає «будь-яка з них» (група or на бекенді) */
            isFilterMultiple: true,
            type: 'select',
            options: USER_ROLE_OPTIONS,
            settings: {
                isFilterLikeSearch: true,
            },
        },
        photo: {
            label: 'Фотографія',
            type: 'file',
            uploadUrl: '/services/user/photo_files',
            uploadAccept: ['jpg', 'jpeg', 'png', 'webp'],
            uploadMaxSizeMb: 3,
        },
        accessProperties: {
            label: 'Властивості доступу',
            type: 'select',
            multiple: true,
            inputProps: { disabled: true },
        },
        externalCode: {
            label: 'Унікальний код ERP',
            type: 'string',
        },
        lastActivityAt: {
            label: 'Остання активність',
            type: 'datetime',
            inputProps: { disabled: true },
        },
        uuid: {
            label: 'UUID',
            inputProps: { disabled: true },
        },
        shortName: {
            label: "Коротке ім'я",
            inputProps: { disabled: true },
        },
        createdAt: {
            label: 'Дата створення',
            type: 'datetime',
            inputProps: { disabled: true },
        },
        updatedAt: {
            label: 'Дата оновлення',
            type: 'datetime',
            inputProps: { disabled: true },
        },
    },
    drawer: {
        /**
         * Не більше 3 полів у рядку (у водія — 4, ресурс успадковує ширину): ширше лише
         * розтягує інпути. 4 поля по ~176px ще вміщуються в мінімум FormRow (150px)
         */
        width: 800,
    },
    table: {
        showColumns: ['fullName', 'roles', 'phone', 'email', 'status', 'createdAt', 'externalCode'],
    },
    filter: {
        includeFields: ['id', 'fullName', 'filterRoles', 'phone', 'email', 'status', 'externalCode'],
    },
    form: {
        template: UserEditTemplate,
        eventHandlers: {
            onBeforeCreateRequestUrl: {
                normalizeRolesPayload: ({ values }: { values: Record<string, any> }) => normalizeRolesPayload(values),
            },
            onBeforeUpdateRequestUrl: {
                normalizeRolesPayload: ({ values }: { values: Record<string, any> }) => normalizeRolesPayload(values),
            },
        },
    },
    restUrl: '/services/users',
    headerPage: {
        eventHandlers: {
            onButtons: {
                InviteButton: ({ buttons, crudContext }: any) => {
                    buttons['InviteButton'] = {
                        label: 'Запросити',
                        icon: <TbMailPlus />,
                        sort: 90,
                        priority: 'button',
                        size: 'middle',
                        type: 'default',
                        depFn: ({ crudContext }: any) =>
                            crudContext?.accessFn?.({ accessMode: 'send_invitation', crudContext }) === true,
                        onClick: () => {
                            crudContext.setState('openInviteDrawer', true)
                        },
                    }
                },
            },
        },
    },
    settings: {
        searchFields: ['id', 'fullName', 'email', 'phone'],
        AddDrawerModeCrudFeature: {
            enableMenuDeleteItem: false,
        },
    },
}
